import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { subscribeStatusChange } from "@/lib/statusBus";

// App-wide watch-status state — a single source of truth so toggling the
// bookmark/watched state on one poster instantly updates every other poster
// for the same title across every rail/page, with no refresh needed
// (2026-09-23). The backend's /library/status holds ONE exclusive `status`
// field (want | watching | watched | null) — so overrides here must mirror
// that as a single value per title, never two independent booleans, or a
// stale "want" override can survive a "watched" write and vice versa,
// leading to a status:null POST that deletes the whole library row while
// the UI still shows the other state as active (2026-10-21).
const WatchlistContext = createContext(null);

function keyOf(item) {
  return `${item.media_type}-${item.tmdb_id}`;
}

function serverStatus(item) {
  return item.status ?? item.my_status ?? null;
}

// Whether `item` carries ANY personal-status info at all (even explicitly
// null, e.g. after a removal) vs. simply never having that field attached
// (plain catalog results from Discover/Trending/search, which never touch
// the user's library). Checking truthiness alone can't tell these apart —
// a correctly-fresh "removed" (null) would otherwise fall through to a
// stale, possibly-still-"want" baseline below (2026-11-02).
function hasServerStatus(item) {
  return item.status !== undefined || item.my_status !== undefined;
}

export function WatchlistProvider({ children }) {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState({}); // key -> "want" | "watching" | "watched" | null
  // Session-wide baseline of the user's REAL statuses, fetched once. Many
  // poster surfaces (Discover, Trending, Home rails, etc.) fetch titles from
  // endpoints that never attach the viewer's personal status at all, so
  // without this, `serverStatus(item)` below silently defaults to "not
  // added" there even when the title genuinely is on the watchlist/watched —
  // and clicking the button then re-POSTs "want", looking like a duplicate
  // add (2026-11-02).
  const [knownStatuses, setKnownStatuses] = useState({});
  const pending = useRef(new Set());

  useEffect(() => {
    if (!user?.user_id) { setKnownStatuses({}); return; }
    client.get("/library/status-map").then((r) => setKnownStatuses(r.data?.results || {})).catch(() => {});
  }, [user?.user_id]);

  // Keep the baseline AND the override in sync with EVERY confirmed
  // watch-status write, including ones made through other code paths
  // entirely (TitleDetail, LogModal, Library pages, CatchUp, etc.), via
  // api.js's response interceptor. Crucially this SETS the override to the
  // confirmed value rather than clearing it — deleting it let a stale
  // item.status snapshot (e.g. a Library page's own un-refetched list) win
  // right after the context's OWN write succeeded, breaking every toggle
  // after the first click (2026-11-02, iteration 665 regression). Setting
  // it instead keeps the override as the single, always-current source of
  // truth for any title touched this session, for both internal and
  // external writes.
  useEffect(() => subscribeStatusChange((key, status) => {
    setKnownStatuses((prev) => ({ ...prev, [key]: status }));
    setOverrides((prev) => ({ ...prev, [key]: status }));
  }), []);

  // Priority: a live override set through THIS context (the most recent,
  // deliberate action, while its own write is still in flight) wins;
  // otherwise a status the CALLING item itself carries (fresh from
  // whatever endpoint just fetched it) beats the baseline below — surfaces
  // that re-fetched their own fresh status must never be overruled by a
  // stale baseline snapshot. The baseline only fills in for surfaces
  // (Discover, Trending, Home rails) whose own item data never carries a
  // personal status at all, and is kept current by the subscription above.
  const statusOf = useCallback((item) => {
    const key = keyOf(item);
    if (key in overrides) return overrides[key];
    if (hasServerStatus(item)) return serverStatus(item);
    return key in knownStatuses ? knownStatuses[key] : null;
  }, [overrides, knownStatuses]);

  const isWantListed = useCallback((item) => statusOf(item) === "want", [statusOf]);
  const isWatching = useCallback((item) => statusOf(item) === "watching", [statusOf]);
  const isWatched = useCallback((item) => statusOf(item) === "watched", [statusOf]);

  const setStatus = useCallback(async (item, nextStatus, successMsg) => {
    const key = keyOf(item);
    if (pending.current.has(key)) return;
    pending.current.add(key);
    const prev = statusOf(item);
    setOverrides((o) => ({ ...o, [key]: nextStatus }));
    try {
      const { data } = await client.post("/library/status", {
        tmdb_id: item.tmdb_id, media_type: item.media_type, title: item.title,
        poster_url: item.poster_url, backdrop_url: item.backdrop_url,
        year: item.year, genres: item.genres, status: nextStatus,
      });
      // A TV show's status can be immediately RE-DERIVED from episode
      // progress right after this write (e.g. clearing "watched" on a
      // fully-logged season snaps straight back to "watched") — only
      // celebrate the change the user actually got, not the one they asked
      // for (2026-11-02, found via live testing).
      if (successMsg && (data?.status ?? null) === nextStatus) toast.success(successMsg);
    } catch {
      setOverrides((o) => ({ ...o, [key]: prev }));
      toast.error("Couldn't update — try again");
    } finally {
      pending.current.delete(key);
    }
  }, [statusOf]);

  // App-wide guard (2026-xx-xx, user-requested): toggleWant used to always
  // force status straight to "want" regardless of the CURRENT status — so
  // bookmarking an already-watched/watching title silently overwrote it,
  // and a second tap (now "want" → null) could delete the whole library
  // doc, tags included. Person.js already had its own bespoke guard for
  // this (shows the bookmark filled+inert there); this makes every OTHER
  // caller (Discover, Home rails, Search, PosterCard, etc.) safe too,
  // from this one shared source of truth, with no per-page changes needed.
  const toggleWant = useCallback((item) => {
    const current = statusOf(item);
    if (current === "watched" || current === "watching") {
      toast.info(`${item.title} is already ${current} — open its title page to change that.`, { id: `watchlist-guard-${item.media_type}-${item.tmdb_id}` });
      return;
    }
    const next = current === "want" ? null : "want";
    setStatus(item, next, next ? `Added ${item.title} to your watch list` : `Removed ${item.title} from your watch list`);
  }, [statusOf, setStatus]);

  const toggleWatched = useCallback((item) => {
    const next = statusOf(item) === "watched" ? null : "watched";
    setStatus(item, next, next ? `Marked ${item.title} as watched` : `Removed ${item.title} from watched`);
  }, [statusOf, setStatus]);

  return (
    <WatchlistContext.Provider value={{ isWantListed, toggleWant, isWatching, isWatched, toggleWatched }}>
      {children}
    </WatchlistContext.Provider>
  );
}

export function useWatchlist() {
  return useContext(WatchlistContext);
}
